import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft, HelpCircle, ShieldCheck, FileText, Sparkles, Mail
} from 'lucide-react';
import { AppBackButton } from '../components/AppBackButton';
import T from '../components/T';


const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <AppBackButton fallbackTo="/settings" className="mb-4" />

      <div className="flex items-center gap-4 mb-4">
        <div className="w-14 h-14 rounded-3xl bg-primary/10 flex items-center justify-center text-primary">
          <HelpCircle size={32} />
        </div>
        <div>
          <h1 className="text-3xl font-medium tracking-[-0.03em] text-ink tracking-[-0.03em]">
            <T k="ui.help.title" />
          </h1>
          <p className="text-xs font-normal text-muted uppercase font-mono tracking-[0.14em]">
            <T k="ui.help.subtitle" />
          </p>
        </div>
      </div>

      <div className="bg-surface rounded-[28px] p-8 md:p-10 border border-black/[0.06] dark:border-white/[0.06] space-y-8">
        
        {/* Support Section */}
        <div className="space-y-4">
          <h3 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-muted">
            <T k="ui.help.support" />
          </h3>
          <div className="p-6 bg-tint-stone/40 rounded-3xl border border-black/[0.06] dark:border-white/[0.06] space-y-3">
            <p className="text-sm font-bold text-ink">
              <T k="ui.help.supportDesc" />
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="mailto:suporte@libritui.com"
                className="flex items-center gap-2 px-6 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[10px] hover:scale-[1.02] active:scale-95 transition-all"
              >
                <Mail size={14} /> <T k="ui.help.contact" />
              </a>
            </div>
          </div>
        </div>

        {/* Legal & Terms Section */}
        <div className="space-y-4 pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
          <h3 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-muted">
            <T k="ui.help.legal" />
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/terms')}
              className="flex items-center justify-between p-6 rounded-3xl bg-tint-stone/40 border border-black/[0.06] dark:border-white/[0.06] hover:border-primary/80 hover:bg-surface transition-all text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-tintd-sky/10 text-tintd-sky flex items-center justify-center">
                  <FileText size={22} />
                </div>
                <div>
                  <h4 className="font-semibold text-sm dark:text-ink group-hover:text-primary transition-colors"><T k="ui.help.terms" /></h4>
                  <p className="text-[10px] text-muted font-bold"><T k="ui.help.termsDesc" /></p>
                </div>
              </div>
              <ChevronLeft size={18} className="rotate-180 text-muted group-hover:text-primary transition-colors" />
            </button>

            <button
              onClick={() => navigate('/privacy')}
              className="flex items-center justify-between p-6 rounded-3xl bg-tint-stone/40 border border-black/[0.06] dark:border-white/[0.06] hover:border-primary/80 hover:bg-surface transition-all text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-ok/10 text-ok flex items-center justify-center">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h4 className="font-semibold text-sm dark:text-ink group-hover:text-primary transition-colors"><T k="ui.help.privacy" /></h4>
                  <p className="text-[10px] text-muted font-bold"><T k="ui.help.privacyDesc" /></p>
                </div>
              </div>
              <ChevronLeft size={18} className="rotate-180 text-muted group-hover:text-primary transition-colors" />
            </button>
          </div>
        </div>

        {/* Beta Sync & Diagnostic Section */}
        <div className="space-y-4 pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
          <h3 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-muted">
            <T k="ui.help.diagSection" />
          </h3>
          <button
            onClick={() => navigate('/settings/beta')}
            className="w-full flex items-center justify-between p-6 rounded-3xl bg-tint-stone/40 border border-black/[0.06] dark:border-white/[0.06] hover:border-warn/30 hover:bg-surface transition-all text-left group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-warn/10 text-warn flex items-center justify-center">
                <Sparkles size={22} />
              </div>
              <div>
                <h4 className="font-semibold text-sm dark:text-ink group-hover:text-warn transition-colors">Beta Sync</h4>
                <p className="text-[10px] text-muted font-bold"><T k="ui.help.diagnostics" /></p>
              </div>
            </div>
            <ChevronLeft size={18} className="rotate-180 text-muted group-hover:text-warn transition-colors" />
          </button>
        </div>

      </div>
    </div>
  );
};

export default HelpPage;
